// Archivo: src/components/Sidebar.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  getPublicServices,
  getPublicServiceSections,
  getSubsectionsBySectionId,
} from "../services/service.service.js";

// Store global
const sidebarState = {
  openServices: new Set(),
  openSections: new Set(),
  loadedSections: {},
  loadedSubsections: {},
  activeSection: null,
  activeService: null,
};

// --- Componente para las Subsecciones ---
const SubsectionItem = ({ serviceId, sectionId, subsection }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    const currentPath = window.location.pathname;
    const targetPath = `/servicios/${serviceId}/secciones/${sectionId}`;

    if (currentPath !== targetPath) {
      navigate(`${targetPath}#subsection-${subsection.id}`);
    } else {
      setTimeout(() => {
        const element = document.getElementById(`subsection-${subsection.id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  return (
    <li>
      <button
        onClick={handleClick}
        className="text-gray-500 hover:text-gob-azul-medio text-sm py-1.5 px-2 block rounded-md transition-colors w-full text-left"
      >
        {subsection.title}
      </button>
    </li>
  );
};

// --- Componente para las Secciones (CORREGIDO) ---
const SectionItem = ({ serviceId, section, isActive }) => {
  const [isOpen, setIsOpen] = useState(
    () => sidebarState.openSections.has(section.id) || isActive,
  );
  const [subsections, setSubsections] = useState(
    () => sidebarState.loadedSubsections[section.id] || [],
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      !isActive &&
      sidebarState.activeSection !== section.id &&
      sidebarState.activeService === serviceId
    ) {
      setIsOpen(false);
      sidebarState.openSections.delete(section.id);
    }
    if (isActive) {
      setIsOpen(true);
      sidebarState.openSections.clear();
      sidebarState.openSections.add(section.id);
      sidebarState.activeSection = section.id;
      sidebarState.activeService = serviceId;
    }
  }, [isActive, section.id, serviceId]);

  useEffect(() => {
    if (isOpen) {
      sidebarState.openSections.add(section.id);
    } else {
      sidebarState.openSections.delete(section.id);
    }
  }, [isOpen, section.id]);

  // FUNCIÓN CORREGIDA: Mejor manejo de errores y estados
  const loadSubsections = useCallback(async () => {
    // Solo cargar si no se ha intentado antes y no está cargando
    if (
      subsections.length === 0 &&
      !isLoading &&
      !sidebarState.loadedSubsections.hasOwnProperty(section.id)
    ) {
      setIsLoading(true);
      try {
        console.log(`Cargando subsecciones para sección ${section.id}`);
        const data = await getSubsectionsBySectionId(section.id);
        console.log(`Subsecciones cargadas para sección ${section.id}:`, data);

        // Asegurar que siempre sea un array
        const subsectionsArray = Array.isArray(data) ? data : [];
        sidebarState.loadedSubsections[section.id] = subsectionsArray;
        setSubsections(subsectionsArray);
      } catch (error) {
        console.error(
          `Error loading subsections for section ${section.id}:`,
          error,
        );
        // Si hay error, marcar como cargado con array vacío para no reintentar
        sidebarState.loadedSubsections[section.id] = [];
        setSubsections([]);
      } finally {
        setIsLoading(false);
      }
    }
  }, [section.id, isLoading, subsections.length]);

  // USEEFFECT CORREGIDO: Con timeout de seguridad
  useEffect(() => {
    if (
      isOpen &&
      subsections.length === 0 &&
      !sidebarState.loadedSubsections.hasOwnProperty(section.id)
    ) {
      // Añadir timeout de seguridad para evitar carga infinita
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          console.warn(`Timeout loading subsections for section ${section.id}`);
          setIsLoading(false);
          sidebarState.loadedSubsections[section.id] = [];
          setSubsections([]);
        }
      }, 5000); // 5 segundos timeout

      loadSubsections().finally(() => {
        clearTimeout(timeoutId);
      });

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, loadSubsections, subsections.length, section.id, isLoading]);

  const toggleSubsections = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen) {
      sidebarState.openSections.clear();
      sidebarState.openSections.add(section.id);
    }
    setIsOpen(!isOpen);
  };

  return (
    <li className="ml-2">
      <div className="flex items-center justify-between group">
        <Link
          to={`/servicios/${serviceId}/secciones/${section.id}`}
          className={`text-gray-600 group-hover:text-gob-azul-medio text-sm py-1.5 px-2 block rounded-md transition-colors flex-grow ${
            isActive ? "bg-blue-50 text-gob-azul-medio font-medium" : ""
          }`}
        >
          {section.title}
        </Link>
        <button
          onClick={toggleSubsections}
          className="p-1 rounded-full hover:bg-gray-200 flex-shrink-0"
          aria-label={isOpen ? "Cerrar subsecciones" : "Abrir subsecciones"}
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* RENDER CORREGIDO: Maneja casos de carga, con datos, sin datos */}
      {isOpen && (
        <ul className="ml-4 mt-1 border-l-2 border-gray-200 pl-2">
          {isLoading ? (
            <li className="text-xs text-gray-400 px-2">Cargando...</li>
          ) : subsections.length > 0 ? (
            subsections.map((sub) => (
              <SubsectionItem
                key={sub.id}
                serviceId={serviceId}
                sectionId={section.id}
                subsection={sub}
              />
            ))
          ) : (
            <li className="text-xs text-gray-500 px-2 italic">
              Sin subsecciones
            </li>
          )}
        </ul>
      )}
    </li>
  );
};

// --- Componente Principal del Sidebar ---
export const Sidebar = ({ isOpen }) => {
  const [services, setServices] = useState([]);
  const [sectionsByService, setSectionsByService] = useState(
    () => sidebarState.loadedSections,
  );
  const [openServices, setOpenServices] = useState(
    () => new Set(sidebarState.openServices),
  );
  const { id: activeServiceId, sectionId } = useParams();
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    getPublicServices().then((data) => setServices(data));
  }, []);

  useEffect(() => {
    if (activeServiceId) {
      const serviceIdNum = parseInt(activeServiceId);
      if (!sidebarState.openServices.has(serviceIdNum)) {
        sidebarState.openServices.add(serviceIdNum);
        setOpenServices(new Set(sidebarState.openServices));
      }
      if (!sidebarState.loadedSections[serviceIdNum]) {
        getPublicServiceSections(serviceIdNum)
          .then((data) => {
            sidebarState.loadedSections[serviceIdNum] = data;
            setSectionsByService({ ...sidebarState.loadedSections });
          })
          .catch((error) => {
            console.error(
              `Error loading sections for service ${serviceIdNum}:`,
              error,
            );
            sidebarState.loadedSections[serviceIdNum] = [];
            setSectionsByService({ ...sidebarState.loadedSections });
          });
      }
    }
    isFirstRender.current = false;
  }, [activeServiceId]);

  const toggleService = useCallback((serviceId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const newOpenServices = new Set(sidebarState.openServices);

    if (newOpenServices.has(serviceId)) {
      newOpenServices.delete(serviceId);
    } else {
      newOpenServices.add(serviceId);
      if (!sidebarState.loadedSections[serviceId]) {
        getPublicServiceSections(serviceId)
          .then((data) => {
            sidebarState.loadedSections[serviceId] = data;
            setSectionsByService({ ...sidebarState.loadedSections });
          })
          .catch((error) => {
            console.error(
              `Error loading sections for service ${serviceId}:`,
              error,
            );
            sidebarState.loadedSections[serviceId] = [];
            setSectionsByService({ ...sidebarState.loadedSections });
          });
      }
    }

    sidebarState.openServices = newOpenServices;
    setOpenServices(new Set(newOpenServices));
  }, []);

  const sidebarClasses = `
    fixed top-16 left-0 h-[calc(100vh-112px)] w-64 p-6 bg-white border-r border-gray-200
    transform transition-transform duration-300 ease-in-out z-20 overflow-y-auto
    ${isOpen ? "translate-x-0" : "-translate-x-full"}
  `;

  return (
    <aside className={sidebarClasses}>
      {/* Imagen con link a Home */}
      <div className="mb-10">
        <Link
          to="/quienes-somos"
          className="block transition-transform duration-300 hover:scale-105"
          aria-label="Ir a la página Quiénes Somos"
        >
          <img
            src="/1.svg"
            alt="Logo Gobierno de Salta"
            className="mx-auto h-16 w-auto"
          />
        </Link>
      </div>
      <nav>
        {/* Sección de links fijos */}
        {/* <div className="mb-8">
          <h3 className="font-semibold text-gray-400 text-xs uppercase tracking-wider mb-3">
            LA SUBSECRETARÍA
          </h3>
          <ul>
            <li>
              <Link 
                to="/quienes-somos" 
                className={`text-gray-600 hover:bg-gray-100 hover:text-gob-azul-medio text-sm py-1.5 px-2 block rounded-md transition-colors ${
                  location.pathname === '/quienes-somos' ? 'bg-gray-100 text-gob-azul-medio font-medium' : ''
                }`}
              >
                Quienes Somos
              </Link>
            </li>
          </ul>
        </div> */}
        {/* Sección de links dinamicos en base a los servicios cargados */}
        <div className="mb-8">
          <h3 className="font-semibold text-gray-400 text-xs uppercase tracking-wider mb-3">
            SERVICIOS
          </h3>
          <ul className="space-y-1">
            {services.map((service) => {
              const isServiceActive = parseInt(activeServiceId) === service.id;
              const isServiceOpen = openServices.has(service.id);

              return (
                <li key={service.id}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/servicios/${service.id}`}
                      className={`flex-grow text-gray-600 hover:bg-gray-100 hover:text-gob-azul-medio text-sm py-1.5 px-2 rounded-md transition-colors ${
                        isServiceActive
                          ? "bg-gray-100 text-gob-azul-medio font-medium"
                          : ""
                      }`}
                    >
                      {service.name}
                    </Link>
                    <button
                      onClick={(e) => toggleService(service.id, e)}
                      className="p-1 rounded-full hover:bg-gray-200 mr-2 flex-shrink-0"
                      aria-label={
                        isServiceOpen ? "Cerrar secciones" : "Abrir secciones"
                      }
                    >
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          isServiceOpen ? "rotate-90" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  {isServiceOpen && (
                    <ul className="mt-2 space-y-1">
                      {(sectionsByService[service.id] || []).map((section) => (
                        <SectionItem
                          key={section.id}
                          serviceId={service.id}
                          section={section}
                          isActive={parseInt(sectionId) === section.id}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
